// VariantPricingCalculator - inline pricing calculator for a single variant
// Margins are stored as percentages applied to (purchase + logistics per plant).
// Local calculation kept in sync with the edge function's percentage formula.
// Updated: saveVariantMargins uses upsert (onConflict variant_id,minimum_quantity) instead of
// delete-then-insert to prevent silent data loss; debounced margin saves; error surfacing
// now includes the underlying database error message so admins can see the real cause.

import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatEuroCents } from '@/lib/plant-pricing';
import { Calculator, ChevronDown, ChevronRight, Loader as Loader2, TriangleAlert as AlertTriangle, RefreshCw, Layers } from 'lucide-react';

interface ProductVariant {
  id?: string;
  size: string;
  type: string;
  price_tier_1: number;
  price_tier_2?: number | null;
  price_tier_3?: number | null;
  price_tier_4?: number | null;
  purchase_price_cents?: number | null;
  plants_per_box?: number | null;
  boxes_per_pallet_position?: number | null;
  price_rounding_increment_cents?: number | null;
  transport_method: string;
  active: boolean;
  [key: string]: any;
}

interface MarginTierValues {
  tier1: number;
  tier2: number;
  tier3: number;
  tier4: number;
}

interface VariantPricingCalculatorProps {
  productId: string | null;
  btwPercentage: number;
  variant: ProductVariant;
  variantIndex: number;
  onUpdatePrices: (index: number, prices: { price_tier_1: number; price_tier_2: number | null; price_tier_3: number | null; price_tier_4: number | null }) => void;
  onUpdatePricingFields: (index: number, fields: Partial<ProductVariant>) => void;
}

const FIXED_RANGES = [
  { min: 25, max: 50, label: '25-50' },
  { min: 51, max: 75, label: '51-75' },
  { min: 76, max: 100, label: '76-100' },
  { min: 101, max: null, label: '101+' },
];

const TIER_QUANTITIES = [25, 51, 76, 101];
const FALLBACK_DEFAULTS: MarginTierValues = { tier1: 30, tier2: 25, tier3: 20, tier4: 15 };

export default function VariantPricingCalculator({
  productId,
  btwPercentage,
  variant,
  variantIndex,
  onUpdatePrices,
  onUpdatePricingFields,
}: VariantPricingCalculatorProps) {
  const [defaultPercentages, setDefaultPercentages] = useState<number[]>([30, 25, 20, 15]);
  const [variantMargins, setVariantMargins] = useState<MarginTierValues | null>(null);
  const [usingDefaults, setUsingDefaults] = useState<boolean>(true);
  const [marginsExpanded, setMarginsExpanded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const [marginInputValues, setMarginInputValues] = useState<(string | null)[]>([null, null, null, null]);
  const calcTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevValuesKey = useRef<string>('');

  useEffect(() => {
    loadDefaultMargins();
  }, []);

  useEffect(() => {
    if (productId && variant.id) {
      loadVariantMargins(variant.id);
    }
  }, [productId, variant.id]);

  useEffect(() => {
    if (!variant.active) return;
    const margins = getEffectiveMargins();
    const key = `${variant.purchase_price_cents}-${variant.plants_per_box}-${variant.boxes_per_pallet_position}-${variant.price_rounding_increment_cents ?? 5}-${margins.tier1}-${margins.tier2}-${margins.tier3}-${margins.tier4}`;
    if (prevValuesKey.current !== key && variant.purchase_price_cents && variant.plants_per_box) {
      triggerCalculation();
    }
    prevValuesKey.current = key;
  }, [variant, variantMargins, usingDefaults, defaultPercentages]);

  function getDefaultMarginValues(): MarginTierValues {
    return {
      tier1: defaultPercentages[0] ?? FALLBACK_DEFAULTS.tier1,
      tier2: defaultPercentages[1] ?? FALLBACK_DEFAULTS.tier2,
      tier3: defaultPercentages[2] ?? FALLBACK_DEFAULTS.tier3,
      tier4: defaultPercentages[3] ?? FALLBACK_DEFAULTS.tier4,
    };
  }

  function getEffectiveMargins(): MarginTierValues {
    if (variantMargins && !usingDefaults) {
      return variantMargins;
    }
    return getDefaultMarginValues();
  }

  async function loadDefaultMargins() {
    const { data } = await supabase
      .from('delivery_settings')
      .select('setting_value')
      .eq('setting_key', 'default_margin_tiers')
      .maybeSingle();
    if (data?.setting_value) {
      const tiers = data.setting_value as { minimum_quantity: number; margin_percentage: number }[];
      const sorted = [...tiers].sort((a, b) => a.minimum_quantity - b.minimum_quantity);
      setDefaultPercentages(sorted.map(t => Number(t.margin_percentage) || 0));
    }
  }

  async function loadVariantMargins(variantId: string) {
    const { data } = await supabase
      .from('plant_margin_tiers')
      .select('minimum_quantity, margin_percentage')
      .eq('variant_id', variantId)
      .eq('active', true)
      .order('minimum_quantity');

    if (data && data.length > 0) {
      const sorted = [...data].sort((a, b) => a.minimum_quantity - b.minimum_quantity);
      const defaults = getDefaultMarginValues();
      setVariantMargins({
        tier1: Number(sorted[0]?.margin_percentage ?? defaults.tier1),
        tier2: Number(sorted[1]?.margin_percentage ?? defaults.tier2),
        tier3: Number(sorted[2]?.margin_percentage ?? defaults.tier3),
        tier4: Number(sorted[3]?.margin_percentage ?? defaults.tier4),
      });
      setUsingDefaults(false);
    } else {
      setUsingDefaults(true);
    }
  }

  async function saveVariantMargins(variantId: string, margins: MarginTierValues) {
    if (!productId) return;

    const values = [margins.tier1, margins.tier2, margins.tier3, margins.tier4];
    const inserts = FIXED_RANGES.map((range, i) => ({
      product_id: productId,
      variant_id: variantId,
      minimum_quantity: range.min,
      maximum_quantity: range.max,
      margin_percentage: values[i],
      sort_order: i + 1,
      active: true,
    }));

    const { error } = await supabase
      .from('plant_margin_tiers')
      .upsert(inserts, { onConflict: 'variant_id,minimum_quantity' })
      .eq('variant_id', variantId);

    if (error) {
      console.error('Failed to save margin tiers:', error);
      setCalculationError(`Marges konden niet worden opgeslagen: ${error.message}`);
    } else {
      setUsingDefaults(false);
      setCalculationError(null);
    }
  }

  function debouncedSaveVariantMargins(variantId: string, margins: MarginTierValues) {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      saveVariantMargins(variantId, margins);
    }, 500);
  }

  async function resetToDefaults(variantId: string) {
    await supabase
      .from('plant_margin_tiers')
      .delete()
      .eq('variant_id', variantId);

    setVariantMargins(null);
    setUsingDefaults(true);
  }

  function handleMarginChange(tierIndex: number, rawValue: string) {
    const newInputs = [...marginInputValues];
    newInputs[tierIndex] = rawValue;
    setMarginInputValues(newInputs);

    if (rawValue === '') return;

    const parsed = parseFloat(rawValue);
    if (isNaN(parsed)) return;

    const current = getEffectiveMargins();
    const updated = { ...current };
    if (tierIndex === 0) updated.tier1 = parsed;
    if (tierIndex === 1) updated.tier2 = parsed;
    if (tierIndex === 2) updated.tier3 = parsed;
    if (tierIndex === 3) updated.tier4 = parsed;
    setVariantMargins(updated);
    setUsingDefaults(false);
    if (variant.id) {
      debouncedSaveVariantMargins(variant.id, updated);
    }
    triggerCalculation();
  }

  function handleMarginBlur(tierIndex: number) {
    const newInputs = [...marginInputValues];
    newInputs[tierIndex] = null;
    setMarginInputValues(newInputs);
  }

  function triggerCalculation() {
    if (!productId) return;
    if (!variant.purchase_price_cents || !variant.plants_per_box) return;

    if (calcTimeout.current) {
      clearTimeout(calcTimeout.current);
    }

    calcTimeout.current = setTimeout(() => {
      calculatePrices();
    }, 800);
  }

  const calculatePricesLocal = useCallback(async () => {
    if (!productId) return;
    if (!variant.purchase_price_cents || !variant.plants_per_box) return;

    setCalculating(true);
    setCalculationError('');

    try {
      const { data: settings } = await supabase
        .from('delivery_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['oegema_pricing', 'kosten_palletbox']);

      const settingsMap: Record<string, any> = {};
      for (const s of settings || []) {
        settingsMap[s.setting_key] = s.setting_value;
      }
      const oegemaPricing = settingsMap['oegema_pricing'] || {};
      const kostenPalletbox = settingsMap['kosten_palletbox']?.cost_per_palletbox_cents || 3000;
      const boxesPerPalletPosition = variant.boxes_per_pallet_position || 2;
      const margins = getEffectiveMargins();

      const prices: (number | null)[] = [];
      for (let i = 0; i < TIER_QUANTITIES.length; i++) {
        const qty = TIER_QUANTITIES[i];
        const percentageForTier = [margins.tier1, margins.tier2, margins.tier3, margins.tier4][i];

        const boxesRequired = Math.ceil(qty / variant.plants_per_box!);
        const palletPositions = Math.ceil(boxesRequired / boxesPerPalletPosition);
        const totalBoxCostCents = boxesRequired * kostenPalletbox;

        let transportCostCents = 0;
        const palletKey = `pallet_place_${palletPositions}`;
        if (palletKey in oegemaPricing) {
          transportCostCents = Math.round(oegemaPricing[palletKey] * 100);
        } else {
          const numericKeys = Object.keys(oegemaPricing)
            .map(k => parseInt(k.replace('pallet_place_', '')))
            .filter(n => !isNaN(n))
            .sort((a, b) => a - b);
          const maxAvailable = numericKeys[numericKeys.length - 1] || 0;
          if (palletPositions > maxAvailable && maxAvailable > 0) {
            const highestKey = `pallet_place_${maxAvailable}`;
            transportCostCents = Math.round(oegemaPricing[highestKey] * 100);
          } else {
            const available = numericKeys.filter(n => n >= palletPositions);
            if (available.length > 0) {
              const nextKey = `pallet_place_${available[0]}`;
              transportCostCents = Math.round(oegemaPricing[nextKey] * 100);
            }
          }
        }

        const totalLogisticsCents = totalBoxCostCents + transportCostCents;
        const logisticsPerPlantCents = Math.ceil(totalLogisticsCents / qty);
        const costBasisCents = variant.purchase_price_cents! + logisticsPerPlantCents;
        const marginPerPlantCents = Math.round(costBasisCents * (percentageForTier / 100));
        const rawSellingPriceCents = costBasisCents + marginPerPlantCents;
        prices.push(rawSellingPriceCents / 100);
      }

      onUpdatePrices(variantIndex, {
        price_tier_1: prices[0] || 0,
        price_tier_2: prices[1],
        price_tier_3: prices[2],
        price_tier_4: prices[3],
      });
    } catch (err: any) {
      setCalculationError(err.message || 'Berekening mislukt');
    } finally {
      setCalculating(false);
    }
  }, [productId, variant, variantMargins, usingDefaults, defaultPercentages, onUpdatePrices, variantIndex]);

  const calculatePrices = useCallback(async () => {
    return calculatePricesLocal();
  }, [calculatePricesLocal]);

  function handlePricingFieldChange(field: string, value: string) {
    const numValue = value === '' ? null : parseInt(value);
    onUpdatePricingFields(variantIndex, { [field]: numValue });
  }

  if (!productId) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Sla het product eerst op om de prijscalculator te gebruiken.
        </AlertDescription>
      </Alert>
    );
  }

  const isConfigured = variant.purchase_price_cents != null && variant.plants_per_box != null;
  const effectiveMargins = getEffectiveMargins();
  const variantId = variant.id || '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm text-gray-700">Prijzen per hoeveelheid</h4>
          {calculating && <Loader2 className="w-3 h-3 animate-spin text-[#5a6552]" />}
          {!calculating && isConfigured && variant.price_tier_1 > 0 && (
            <Badge className="bg-[#e7e1d6] text-[#1e2b22] text-[10px]">Berekend</Badge>
          )}
        </div>
        {isConfigured && (
          <Button
            size="sm"
            variant="ghost"
            onClick={calculatePrices}
            disabled={calculating}
            className="h-7 text-xs"
          >
            <Calculator className="w-3 h-3 mr-1" />
            Herbereken
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">Inkoopprijs (ct) *</Label>
          <Input
            type="number"
            className="h-8 text-sm"
            value={variant.purchase_price_cents ?? ''}
            onChange={e => handlePricingFieldChange('purchase_price_cents', e.target.value)}
            placeholder="bijv. 830"
          />
          {variant.purchase_price_cents != null && (
            <p className="text-[10px] text-gray-400">= {formatEuroCents(variant.purchase_price_cents)}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">Planten/box *</Label>
          <Input
            type="number"
            className="h-8 text-sm"
            value={variant.plants_per_box ?? ''}
            onChange={e => handlePricingFieldChange('plants_per_box', e.target.value)}
            placeholder="40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">Boxen/palletplaats</Label>
          <Input
            type="number"
            className="h-8 text-sm"
            value={variant.boxes_per_pallet_position ?? ''}
            onChange={e => handlePricingFieldChange('boxes_per_pallet_position', e.target.value)}
            placeholder="2"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">Afronding (ct)</Label>
          <Input
            type="number"
            min={0}
            className="h-8 text-sm"
            value={variant.price_rounding_increment_cents ?? 5}
            onChange={e => handlePricingFieldChange('price_rounding_increment_cents', e.target.value)}
            placeholder="5"
          />
        </div>
      </div>

      <Collapsible open={marginsExpanded} onOpenChange={setMarginsExpanded}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2 w-full justify-between">
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3" />
              <span>Margetabellen</span>
              {usingDefaults ? (
                <Badge variant="secondary" className="text-[9px] ml-1">Standaard</Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] ml-1">Aangepast</Badge>
              )}
            </span>
            {marginsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-3 bg-gray-50 rounded-md border border-gray-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs py-1">Staffel</TableHead>
                  <TableHead className="text-xs py-1">Marge (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FIXED_RANGES.map((range, tierIndex) => {
                  const marginValues = [effectiveMargins.tier1, effectiveMargins.tier2, effectiveMargins.tier3, effectiveMargins.tier4];
                  return (
                    <TableRow key={tierIndex}>
                      <TableCell className="text-xs py-1 font-medium">{range.label}</TableCell>
                      <TableCell className="py-1">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step="0.01"
                            className="h-7 w-24 text-xs"
                            value={marginInputValues[tierIndex] !== null ? marginInputValues[tierIndex]! : (marginValues[tierIndex] ?? '')}
                            onChange={(e) => {
                              handleMarginChange(tierIndex, e.target.value);
                            }}
                            onBlur={() => handleMarginBlur(tierIndex)}
                            disabled={false}
                          />
                          <span className="text-xs text-gray-500">%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!usingDefaults && (
              <Button
                variant="link"
                size="sm"
                className="text-xs text-gray-500 mt-2 p-0 h-auto"
                onClick={() => {
                  if (variantId) {
                    resetToDefaults(variantId);
                  } else {
                    setVariantMargins(null);
                    setUsingDefaults(true);
                  }
                }}
              >
                Terug naar standaard
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {calculationError && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-3 w-3" />
          <AlertDescription className="text-xs">{calculationError}</AlertDescription>
        </Alert>
      )}

      {(variant.price_tier_1 > 0 || calculating) && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: '25-50', price: variant.price_tier_1, color: 'bg-[#e7e1d6]/40 border-[#e7e1d6]' },
            { label: '51-75', price: variant.price_tier_2, color: 'bg-[#dcd8ce]/50 border-[#dcd8ce]' },
            { label: '76-100', price: variant.price_tier_3, color: 'bg-[#f5eede]/60 border-[#e7d9c0]' },
            { label: '101+', price: variant.price_tier_4, color: 'bg-[#eee0cf]/60 border-[#d9c1a2]' },
          ].map(({ label, price, color }, i) => (
            <div key={i} className={`p-2 rounded border ${color} text-center transition-opacity ${calculating ? 'opacity-40' : ''}`}>
              <p className="text-[10px] text-gray-500 font-medium">{label}</p>
              {calculating ? (
                <p className="text-sm font-bold text-gray-400 animate-pulse">...</p>
              ) : (
                <p className="text-sm font-bold">
                  {price != null ? `\u20AC${price.toFixed(2)}` : '-'}
                </p>
              )}
              <p className="text-[10px] text-gray-400">excl. BTW</p>
            </div>
          ))}
        </div>
      )}

      {calculating && (
        <p className="text-xs text-[#5a6552] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Prijzen worden herberekend...
        </p>
      )}

      {!isConfigured && (
        <p className="text-xs text-[#c8a94a]">Vul inkoopprijs en planten/box in om automatisch te berekenen.</p>
      )}
    </div>
  );
}
